import { ClerkProvider } from "@clerk/tanstack-react-start";
import FontDMSans from "@fontsource-variable/dm-sans?url";
import FontInterVariableItalic from "@fontsource-variable/inter/opsz-italic.css?url";
import FontInterVariable from "@fontsource-variable/inter/opsz.css?url";
import FontMontserrat from "@fontsource-variable/montserrat?url";
import FontWorkSans from "@fontsource-variable/work-sans?url";
import { context, defaultTextMapSetter, propagation } from "@opentelemetry/api";
import { wrapCreateRootRouteWithSentry } from "@sentry/tanstackstart-react";
import type { QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { MotionConfig } from "motion/react";
import * as React from "react";
import { lazy, Suspense } from "react";

import { AuthKeepalive } from "~/components/auth-keepalive";
import { ClerkStatusHandler } from "~/components/clerk-status-handler";
import { DefaultCatchBoundary } from "~/components/DefaultCatchBoundary";
import { NotFound } from "~/components/NotFound";
import { ReferralCapture } from "~/components/referral-capture";
import { TooltipProvider } from "~/components/ui/tooltip";
import { ConsentProvider } from "~/contexts/consent";
import { TrackingProvider } from "~/contexts/tracking";
import { rethrowClerkAuthError } from "~/lib/auth/clerk-handshake";
import { KeyProvider } from "~/lib/crypto/key-context";
import { getConsentState } from "~/lib/functions/consent";
import { appLegacyRedirect } from "~/lib/shared/legacy-redirects";
import { createBrandOrganizationStructuredData } from "~/lib/shared/meta";
import { clientEnv } from "~/lib/utils/client-env";
import CSS from "~/styles/globals.css?url";

const LazyToaster = lazy(() =>
  import("sonner").then((mod) => ({ default: mod.Toaster })),
);

export const fetchClerkAuth = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ userId: string | null }> => {
    const { auth } = await import("@clerk/tanstack-react-start/server");
    try {
      const { userId } = await auth();

      return {
        userId,
      };
    } catch (error) {
      // Clerk throws a 307 handshake `Response` when the session cookie is
      // stale. Convert it to a router redirect so it survives seroval over RPC
      // (a raw `Response` crashes with "Seroval Error") and the browser can
      // complete the handshake.
      rethrowClerkAuthError(error);
    }
  },
);

export const Route = wrapCreateRootRouteWithSentry(createRootRouteWithContext)<{
  queryClient: QueryClient;
}>()({
  // A retired address that a click inside the app reaches, from an old NOVA
  // answer or a saved notification, opens its page instead of "not found".
  // The Worker edge redirects the same addresses on a full page load.
  beforeLoad: ({ location }) => {
    const destination = appLegacyRedirect(location.href);
    if (destination) throw redirect({ href: destination, replace: true });
  },
  // The region, saved choice and Global Privacy Control signal come with the
  // first HTML, so the cookie banner renders on the server and never flashes.
  // They hold for the visit: client navigations do not ask again.
  loader: async () => ({ consent: await getConsentState() }),
  shouldReload: false,
  head: () => {
    const carrier: { [key: string]: string } = {};
    propagation.inject(context.active(), carrier, defaultTextMapSetter);

    return {
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
        // Default title for any page that doesn't set its own — notably
        // not-found states, which otherwise ship without a <title> at all.
        // Route-level head() titles override this.
        {
          title: "Dream Coach Match",
        },
        ...Object.entries(carrier).map(([k, v]) => ({
          name: k,
          content: v,
        })),
      ],
      links: [
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
        { rel: "icon", href: "/favicon.ico" },
        { rel: "stylesheet", href: CSS },
        { rel: "stylesheet", href: FontInterVariable },
        { rel: "stylesheet", href: FontInterVariableItalic },
        { rel: "stylesheet", href: FontDMSans },
        { rel: "stylesheet", href: FontWorkSans },
        { rel: "stylesheet", href: FontMontserrat },
      ],
      // Analytics and ad tools are not here: they start only with the
      // visitor's consent, from ConsentProvider (~/contexts/consent).
      scripts: [
        // Canonical Organization JSON-LD on every route so Google and the AI
        // engines see one consistent organization site-wide. Pages that need
        // richer schema (WebSite + SearchAction, BreadcrumbList, Person) layer
        // it on top — the shared @id consolidates everything onto one entity.
        {
          type: "application/ld+json",
          children: JSON.stringify(createBrandOrganizationStructuredData()),
        },
      ],
    };
  },
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootComponent() {
  const { consent } = Route.useLoaderData();

  return (
    <ClerkProvider publishableKey={clientEnv.VITE_CLERK_PUBLISHABLE_KEY}>
      <AuthKeepalive />
      <ClerkStatusHandler />
      <TooltipProvider>
        <KeyProvider>
          <RootDocument>
            <ConsentProvider state={consent}>
              <TrackingProvider>
                <ReferralCapture />
                <Outlet />
              </TrackingProvider>
            </ConsentProvider>
          </RootDocument>
        </KeyProvider>
      </TooltipProvider>
    </ClerkProvider>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="bg-background font-sans text-foreground antialiased">
        <MotionConfig reducedMotion="user">{children}</MotionConfig>
        <Suspense fallback={null}>
          <LazyToaster className="fixed" richColors />
        </Suspense>
        <Scripts />
      </body>
    </html>
  );
}
